import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiServer, FiCheck, FiArrowRight, FiShield, FiZap, FiHeadphones, FiSearch } from 'react-icons/fi';
import Navigation from '../components/Navigation';

export default function Landing() {
  const navigate = useNavigate();
  const [domainSearch, setDomainSearch] = useState('');

  const handleDomainSearch = () => {
    if (domainSearch.trim()) {
      navigate(`/products?domain=${domainSearch}`);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero with Domain Search */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Your Domain & Hosting Together
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start your online presence with our reliable hosting and domain registration. Simple, fast, and affordable.
            </p>
          </div>

          {/* Domain Search Box */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-3.5 text-gray-400" size={20} />
                <input
                  type="text"
                  value={domainSearch}
                  onChange={(e) => setDomainSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleDomainSearch()}
                  placeholder="Find your domain: e.g., mywebsite..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-lg"
                />
              </div>
              <button
                onClick={handleDomainSearch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-all"
              >
                Search
              </button>
            </div>
            <p className="text-gray-600 text-sm mt-3">💡 Popular: business.com  startup.io  creative.net</p>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold"
            >
              Browse all products
              <FiArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">Why Choose Maestrohosts?</h2>
          <p className="text-gray-600 text-center text-lg mb-12 max-w-2xl mx-auto">
            We make it easy to get online and grow your business
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="p-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <FiZap className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600">
                Blazing fast performance with our optimized servers. Your visitors will love the speed.
              </p>
            </div>

            <div className="p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <FiShield className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Secure & Safe</h3>
              <p className="text-gray-600">
                Enterprise security with SSL certificates, daily backups, and DDoS protection included.
              </p>
            </div>

            <div className="p-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <FiHeadphones className="text-purple-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Support</h3>
              <p className="text-gray-600">
                Our team is always ready to help. Get answers fast from our knowledgeable support team.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 text-center border border-blue-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">✨ More Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">Free domain email</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">One-click WordPress</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">Unlimited databases</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">Money-back guarantee</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">Auto-scaling</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-600" size={20} />
                <span className="text-gray-700">CDN included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">Plans for Every Website</h2>
          <p className="text-gray-600 text-center text-lg mb-12">
            Simple, transparent pricing. No hidden fees.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              {
                name: 'Starter',
                price: '$4.99',
                storage: '10GB',
                features: ['Perfect to start', 'Great for small sites'],
              },
              {
                name: 'Professional',
                price: '$9.99',
                storage: '50GB',
                popular: true,
                features: ['Growing businesses', 'Email marketing'],
              },
              {
                name: 'Business',
                price: '$19.99',
                storage: '200GB',
                features: ['High traffic sites', 'Email campaigns'],
              },
            ].map((plan, idx) => (
              <div
                key={idx}
                className={`rounded-xl p-6 ${
                  plan.popular
                    ? 'bg-blue-600 text-white shadow-2xl ring-2 ring-blue-400 scale-105'
                    : 'bg-white border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <span className="inline-block bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-xs font-bold mb-3">
                    MOST POPULAR
                  </span>
                )}
                <h3 className="text-2xl font-bold mb-1">{plan.name}</h3>
                <p className={plan.popular ? 'text-blue-100 text-sm mb-4' : 'text-gray-600 text-sm mb-4'}>
                  {plan.features[0]}
                </p>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className={plan.popular ? 'text-blue-100' : 'text-gray-600'}>/month</span>
                </div>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-blue-100' : 'text-gray-600'}`}>
                  {plan.storage} Storage
                </p>
                <button
                  onClick={() => navigate('/products')}
                  className={`w-full py-3 rounded-lg font-bold transition-all ${
                    plan.popular
                      ? 'bg-white text-blue-600 hover:bg-gray-100'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/products')}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-lg"
            >
              View all plans & features
              <FiArrowRight size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Online Today</h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of websites powered by Maestrohosts. Free domain with annual plans.
          </p>
          <button
            onClick={() => navigate('/products')}
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-all text-lg"
          >
            Browse All Products
            <FiArrowRight size={24} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Web Hosting</a></li>
                <li><a href="#" className="hover:text-white">Domains</a></li>
                <li><a href="#" className="hover:text-white">Email</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Status</a></li>
                <li><a href="#" className="hover:text-white">API Docs</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy</a></li>
                <li><a href="#" className="hover:text-white">Terms</a></li>
                <li><a href="#" className="hover:text-white">Cookies</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2026 Maestrohosts. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
